import {
    useEffect,
    useMemo,
    useState,
    useCallback,
    DependencyList,
} from 'react';
import { useMachine } from '@xstate/react';

import {
    getPreFilterMachine,
    mapParamFiltersToPreFilterValues,
    mapPreFilterConfigsToParamInputs,
    getBuildEnvironment,
} from '@trqk/utils';
import { useUrlParamFilters } from '.';

const buildEnvironment = getBuildEnvironment();

interface UsePreFilteredListInput<TData extends Record<string, unknown>> {
    /**
     * Map of {@link PreFilterConfig} objects for setting up the filters
     */
    preFilterConfigs: Map<string, PreFilterConfig>;
    /**
     * Function that accepts an array of dependencies and returns a Promise which resolves to the
     * list of all items (unfiltered). Will be called every time the `fetchAllItemsDeps` changes via
     * a useEffect() call.
     */
    fetchAllItems(dependencies: DependencyList): Promise<TData[]>;
    /**
     * Object of dependencies for the `fetchAllItems` function.
     */
    fetchAllItemsDeps: DependencyList;
    /**
     * Error handler for the fetchAllItems promise
     */
    onFetchAllItemsError?(error: Error): void;
    /**
     * Called after the `fetchAllItems` function returns
     * @param next - the next `allItems` array returned by `fetchAllItems`
     * @param send - method for sending events to the preFilter machine
     */
    onFetchAllItemsSuccess?(
        next?: TData[],
        send?: SendPreFilterEvent<TData>,
    ): void;
}

interface UsePreFilteredListResult<TData extends Record<string, unknown>> {
    /**
     * Current context of the preFilter machine
     */
    current: PreFilterState<TData>;
    /**
     * Method for sending events to the preFilter machine
     */
    send: SendPreFilterEvent<TData>;
}

/**
 * Utilizes a preFilter machine to provide filtering that is applied before the data is passed to a
 * react-table instance. See notes in {@link getPreFilterMachine}.
 * @param input - {@link UsePreFilteredListInput}
 * @returns the {@link UsePreFilteredListResult}
 */
export function usePreFilteredList<TData extends Record<string, unknown>>({
    fetchAllItems,
    fetchAllItemsDeps,
    preFilterConfigs,
    onFetchAllItemsError,
    onFetchAllItemsSuccess,
}: UsePreFilteredListInput<TData>): UsePreFilteredListResult<TData> {
    const [current, send] = useMachine(
        getPreFilterMachine<TData>({
            preFilterConfigs,
        }),
    );

    const paramInputs = useMemo(
        () => mapPreFilterConfigsToParamInputs(preFilterConfigs),
        [preFilterConfigs],
    );

    const onFilterUpdate = useCallback(
        (nextFilters: URLParamFilter[], isInitialRead: boolean): void => {
            if (isInitialRead) {
                const preFilterValues =
                    mapParamFiltersToPreFilterValues(nextFilters);

                send({
                    type: 'SET_PRE_FILTER_VALUES',
                    value: {
                        preFilterValues,
                    },
                });
            }
        },
        [send],
    );

    useUrlParamFilters({
        paramInputs,
        onFilterUpdate,
    });

    const [isInitialQuery, setIsInitialQuery] = useState(true);

    const depsAsString = JSON.stringify(fetchAllItemsDeps);

    useEffect(() => {
        let depsParsed = [];

        try {
            depsParsed = JSON.parse(depsAsString);

            fetchAllItems(depsParsed)
                .then((allItems: TData[]): void => {
                    send({
                        type: 'SET_ALL_ITEMS_AND_SET_DROPDOWN_OPTIONS',
                        value: {
                            allItems,
                            isInitialQuery,
                        },
                    });

                    setIsInitialQuery(false);

                    if (onFetchAllItemsSuccess) {
                        onFetchAllItemsSuccess(allItems, send);
                    }
                })
                .catch((error: Error): void => {
                    if (onFetchAllItemsError) {
                        onFetchAllItemsError(error);
                    }
                });
        } catch {
            // If we can't parse, there is un-serialize-able data in the fetchAllItemsDeps
            if (buildEnvironment !== 'production') {
                // eslint-disable-next-line no-console
                console.error(
                    'Could not use JSON.stringify to serialize fetchAllItemsDeps',
                );
            }
        }
    }, [depsAsString, send]); // JSON.stringify keeps fetchAllItemsDeps from triggering extra
    // fetches when its value is the same but an object reference changes.

    const [showFilters, setShowFilters] = useState(false);

    useEffect(() => {
        // Show filters if there are any filters set by URL parameters
        if (!showFilters && current.context.preFilterValues.size > 0) {
            setShowFilters(true);
        }
    }, [current.context.preFilterValues.size, showFilters]);

    return { current, send };
}
